import React, { useState } from 'react';
import { X, Save, User, Mail, Phone, Building, Link as LinkIcon, FileText } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { Prospect, Metadata } from './types';

interface ProspectEditFormProps {
  prospect: Prospect;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedProspect: Prospect) => void;
}

const ProspectEditForm: React.FC<ProspectEditFormProps> = ({
  prospect,
  isOpen,
  onClose,
  onSave
}) => {
  // Initialize form data with top-level fields and metadata fields
  const [formData, setFormData] = useState({
    // Top-level fields
    prenom: prospect.prenom || '',
    nom: prospect.nom || '',
    email: prospect.email || '',
    linkedin: prospect.linkedin || '',
    site_url: prospect.site_url || '',
    notes: prospect.notes || '',
    // Metadata fields (all non-top-level fields)
    telephone: prospect.metadata?.telephone || prospect.telephone || '',
    poste: prospect.metadata?.poste || prospect.poste || '',
    source: prospect.metadata?.source || prospect.source || '',
    company: prospect.metadata?.company || '',
    industry: prospect.metadata?.industry || '',
    location: prospect.metadata?.location || '',
    companySize: prospect.metadata?.companySize || '',
    description: prospect.metadata?.description || '',
    companyLocation: prospect.metadata?.companyLocation || '',
    companyDescription: prospect.metadata?.companyDescription || '',
    title: prospect.metadata?.title || '',
    summary: prospect.metadata?.summary || '',
    domain_emelia: prospect.metadata?.domain_emelia || '',
    estimatedMaxRevenue: prospect.metadata?.estimatedMaxRevenue || '',
    estimatedMinRevenue: prospect.metadata?.estimatedMinRevenue || '',
  });
  
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Prepare metadata with all non-top-level fields
      const metadata: Metadata = {
        // Move all non-top-level fields to metadata
        telephone: formData.telephone || undefined,
        poste: formData.poste || undefined,
        source: formData.source || undefined,
        company: formData.company || undefined,
        industry: formData.industry || undefined,
        location: formData.location || undefined,
        companySize: formData.companySize || undefined,
        description: formData.description || undefined,
        companyLocation: formData.companyLocation || undefined,
        companyDescription: formData.companyDescription || undefined,
        title: formData.title || undefined,
        summary: formData.summary || undefined,
        domain_emelia: formData.domain_emelia || undefined,
        estimatedMaxRevenue: formData.estimatedMaxRevenue || undefined,
        estimatedMinRevenue: formData.estimatedMinRevenue || undefined,
        // Preserve any existing metadata that we're not explicitly setting
        ...prospect.metadata,
        // Override with our form values (in case they were in metadata before)
        ...Object.fromEntries(
          Object.entries({
            telephone: formData.telephone,
            poste: formData.poste,
            source: formData.source,
            company: formData.company,
            industry: formData.industry,
            location: formData.location,
            companySize: formData.companySize,
            description: formData.description,
            companyLocation: formData.companyLocation,
            companyDescription: formData.companyDescription,
            title: formData.title,
            summary: formData.summary,
            domain_emelia: formData.domain_emelia,
            estimatedMaxRevenue: formData.estimatedMaxRevenue,
            estimatedMinRevenue: formData.estimatedMinRevenue,
          }).filter(([_, v]) => v !== undefined && v !== '')
        )
      };

      // Update the prospect in the database
      // Only include top-level fields that exist in the database schema
      const { data, error } = await supabase
        .from('prospects')
        .update({
          prenom: formData.prenom,
          nom: formData.nom,
          email: formData.email,
          linkedin: formData.linkedin || null, // Ensure null instead of empty string for nullable fields
          site_url: formData.site_url,
          notes: formData.notes || null, // Ensure null instead of empty string for nullable fields
          metadata: Object.keys(metadata).length > 0 ? metadata : null, // Only include metadata if it has values
          updated_at: new Date().toISOString()
        })
        .eq('id', prospect.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Notifier le composant parent
      onSave(data);
      onClose();
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde du prospect');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Modifier le prospect
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Informations personnelles */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center">
              <User className="w-4 h-4 mr-2" />
              Informations personnelles
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom
                </label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Poste
                </label>
                <input
                  type="text"
                  name="poste"
                  value={formData.poste}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <input
                type="text"
                name="source"
                value={formData.source}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="ex: LinkedIn, Site web, Référence..."
              />
            </div>
          </div>

          {/* Informations de contact */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center">
              <LinkIcon className="w-4 h-4 mr-2" />
              Liens et contact
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn
              </label>
              <input
                type="url"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://linkedin.com/in/..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site web
              </label>
              <input
                type="url"
                name="site_url"
                value={formData.site_url}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://example.com"
              />
            </div>
          </div>

          {/* Informations entreprise */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center">
              <Building className="w-4 h-4 mr-2" />
              Informations entreprise
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entreprise
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secteur d'activité
                </label>
                <input
                  type="text"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Localisation
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taille entreprise
                </label>
                <input
                  type="text"
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ex: 10-50 employés"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Localisation entreprise
              </label>
              <input
                type="text"
                name="companyLocation"
                value={formData.companyLocation}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description entreprise
              </label>
              <textarea
                name="companyDescription"
                value={formData.companyDescription}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Revenu estimé minimum
                </label>
                <input
                  type="text"
                  name="estimatedMinRevenue"
                  value={formData.estimatedMinRevenue}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ex: 100000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Revenu estimé maximum
                </label>
                <input
                  type="text"
                  name="estimatedMaxRevenue"
                  value={formData.estimatedMaxRevenue}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ex: 500000"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Domaine Emelia
              </label>
              <input
                type="text"
                name="domain_emelia"
                value={formData.domain_emelia}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Résumé et description */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Résumé et description
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Résumé
              </label>
              <textarea
                name="summary"
                value={formData.summary}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              ></textarea>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              ></textarea>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description entreprise
              </label>
              <textarea
                name="companyDescription"
                value={formData.companyDescription}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              ></textarea>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Notes
            </h3>
            
            <div>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Notes sur le prospect..."
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProspectEditForm;
